import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import type { AppModule } from "./appModules";
import { useAuth } from "./useAuth";

/** Message inside the app layout (sidebar stays visible). */
export function AccessMessage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md text-center">
        <p className="text-base font-semibold text-foreground">{title}</p>
        <div className="mt-3 text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

/**
 * Route guard by page: shows the page only when the user's access groups
 * include it (platform admins have every page). The API enforces the same
 * rule on the page's data; this only decides what to show.
 */
export default function RequireModule({
  module,
  children,
}: {
  module: AppModule;
  children: ReactNode;
}) {
  const { isPending, hasModule } = useAuth();

  if (isPending) return <Navigate to="/pending" replace />;
  if (!hasModule(module)) {
    return (
      <AccessMessage title="You don't have access to this page">
        <p>Ask your MDF administrator if you need it.</p>
        <Link to="/" replace className={buttonVariants({ variant: "outline", className: "mt-4" })}>
          Go to my pages
        </Link>
      </AccessMessage>
    );
  }
  return children;
}
